import { QueryParams } from 'common/urls/QueryParams';
import { DateTime } from 'luxon';
import React from 'react';
import { RouteChildrenProps } from 'react-router';
import { Routes } from 'src/shared/Routes';
import { getIdSluggedPath } from 'src/shared/utils/RouteUtilities';
import { useSelector } from 'src/store';
import { selectShefDeliveryDateCapacityInfo } from 'src/store/cart/selectors';
import { CartShef, WholeCartLineItem } from 'src/store/cart/types';
import styled from 'styled-components';
import { Colors, Spacing } from 'web-common/src/shared/styles';
import { MultiCartLineItem } from './MultiCartLineItem';
import { ShefOrderBlockHeader } from './ShefOrderBlockHeader';

export const OrderBlockContainer = styled.div`
  & + & {
    margin-top: ${Spacing.SINGLE_HALF};
  }
`;

const LineItemContainer = styled.div`
  border-top: 1px solid ${Colors.GRAY_LITE_20};

  &:first-child {
    border-top: none;
  }
`;

interface OrderBlockProps {
  shef: CartShef;
  deliveryDate: string;
  // shef and delivery date selected if present
  lineItems: WholeCartLineItem[];
  unavailableLineItemIds: Set<number>;
  availableLineItemCapacities: Record<number, { numAvailable: number | null; numRemaining: number | null }>;
  setActiveShefDeliveryDate: (deliveryDate: DateTime) => void;
  isEditingOrder: boolean;
  onEditDeliveryDate: () => void;
  match: RouteChildrenProps['match'];
}

export const OrderBlock: React.FC<OrderBlockProps> = ({
  shef,
  setActiveShefDeliveryDate,
  lineItems,
  unavailableLineItemIds,
  availableLineItemCapacities,
  deliveryDate,
  isEditingOrder,
  onEditDeliveryDate,
  match,
}) => {
  const { numDishesRemaining, numDishesAvailable } = useSelector((state) =>
    selectShefDeliveryDateCapacityInfo(state.cart, shef.id, isEditingOrder, deliveryDate)
  );

  // TODO: need to get loading state from jess
  if (shef.loading) {
    return <div>Loading...</div>;
  }

  const shefMenuUrl = getIdSluggedPath(Routes.CONSUMER_ORDER_ONE_TIME, shef.id, {
    [QueryParams.DATE]: deliveryDate,
  });

  return (
    <OrderBlockContainer>
      <div>
        <ShefOrderBlockHeader
          shef={shef}
          shefMenuUrl={shefMenuUrl}
          setActiveShefDeliveryDate={setActiveShefDeliveryDate}
          shefNumDishesAvailable={numDishesAvailable}
          shefNumDishesRemaining={numDishesRemaining}
          onEditDeliveryDate={onEditDeliveryDate}
          isEditingOrder={isEditingOrder}
          match={match}
        />
        <div>
          {lineItems.map((lineItem) => (
            <LineItemContainer key={lineItem.id}>
              <MultiCartLineItem
                deliveryDate={deliveryDate}
                lineItem={lineItem}
                shefId={shef.id}
                unavailable={unavailableLineItemIds.has(lineItem.id)}
                numAvailable={availableLineItemCapacities[lineItem.id].numAvailable ?? Infinity}
                shefMenuUrl={shefMenuUrl}
                shefNumDishesAvailable={numDishesAvailable}
                shefNumDishesRemaining={numDishesRemaining}
                isEditingOrder={isEditingOrder}
                paddingX={Spacing.SINGLE}
                paddingY={Spacing.HALF}
              />
            </LineItemContainer>
          ))}
        </div>
      </div>
    </OrderBlockContainer>
  );
};
